import {EChartsOption, SeriesOption} from 'echarts';
import {PieChart, PieSeriesOption} from 'echarts/charts';
import {LegendComponent, TitleComponent, TooltipComponent} from 'echarts/components';
import * as echarts from 'echarts/core';
import {CanvasRenderer} from 'echarts/renderers';
import Vue from 'vue';
import {Component, Prop, Ref, Watch} from 'vue-property-decorator';

echarts.use([TitleComponent, TooltipComponent, LegendComponent, PieChart, CanvasRenderer]);

interface PieChartDataItem {
    value: number;
    name: string;
}

interface ChartSeriesData extends Omit<PieSeriesOption, 'data'> {
    name: string;
    type: 'pie';
    radius?: string | string[];
    data: PieChartDataItem[];
    emphasis?: PieSeriesOption['emphasis'];
}

interface ChartData {
    title?: string;
    subtext?: string;
    seriesData: ChartSeriesData;
    legendOrient?: 'vertical' | 'horizontal';
    legendLeft?: string | number;
    legendTop?: string | number;
}

@Component
export default class DustAreaEquipmentsCountChart extends Vue {
    @Prop({required: true, type: Object as () => ChartData})
    readonly chartData!: ChartData;

    @Ref('chartContainer')
    readonly chartContainer!: HTMLDivElement;

    private chartInstance: echarts.ECharts | null = null;

    public chartHeight: string = '200px';
    public chartWidth: string = '100%';

    mounted() {
        this.initChart();
        window.addEventListener('resize', this.handleResize);
    }

    beforeDestroy() {
        if (this.chartInstance) {
            this.chartInstance.dispose();
            this.chartInstance = null;
        }
        window.removeEventListener('resize', this.handleResize);
    }

    @Watch('chartData', {deep: true})
    onChartDataChanged(newData: ChartData) {
        if (this.chartInstance && newData) {
            this.updateChartOptions();
        } else if (!newData && this.chartInstance) {
            this.chartInstance.clear();
        } else if (newData && !this.chartInstance) {
            this.initChart();
        }
    }

    private initChart(): void {
        if (this.chartContainer) {
            if (this.chartInstance) {
                this.chartInstance.dispose();
            }
            this.chartInstance = echarts.init(this.chartContainer);
            this.updateChartOptions();
        } else {
            console.error('Chart container element not found.');
        }
    }

    private updateChartOptions(): void {
        if (!this.chartInstance || !this.chartData || !this.chartData.seriesData) {
            console.warn('Chart instance or chart data/seriesData is not available.');
            return;
        }

        const option: EChartsOption = {
            type: 'pie',
            labelLine: {
                lineStyle: {
                    color: '#235894',
                },
            },
            tooltip: {
                trigger: 'item',
                formatter: '{a} <br/>{b}: {c} ({d}%)',
            },
            series: [
                {
                    ...this.chartData.seriesData,
                    radius: this.chartData.seriesData.radius || ['40%', '70%'],
                    label: {
                        show: true,
                        bleedMargin: 10,
                    },
                    avoidLabelOverlap: false,
                    emphasis: this.chartData.seriesData.emphasis || {},
                },
            ] as SeriesOption[],
        };

        this.chartInstance.setOption(option, true);
    }

    private handleResize(): void {
        this.chartInstance?.resize();
    }

    get containerStyle() {
        return {
            width: this.chartWidth,
            height: this.chartHeight,
        };
    }
}
